import React, { useCallback, useEffect, useState } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { apiClient } from "@/lib/apiClient";
import { Switch } from "@/components/ui/switch";
import { Scale, ShieldAlert, FileText, CalendarClock, CheckCircle2, Banknote } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(n || 0);

/** Bezugsteuer-Schwelle CHF 10'000 (nicht MWST-registrierte Firmen): Zählstand, «es fehlen noch …», manuelle Aktivierung, Pflicht-Hinweis ab Schwelle. */
export const ReverseChargeThresholdCard = ({ companyId, compact = false }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ date: new Date().toISOString().slice(0, 10), bank_account_id: "1020", amount: "", reference: "" });
  const load = useCallback(() => {
    if (!companyId) return;
    apiClient.get(`/companies/${companyId}/vat/reverse-charge-status`).then((r) => setSt(r.data)).catch(() => setSt(null));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("inbox:changed", h);
    return () => window.removeEventListener("inbox:changed", h);
  }, [load]);
  if (!st || st.vat_registered) return null;
  const due = st.report_due;
  if (!compact && !st.count && !st.forced && !due) return null;
  const exceeded = st.level === "exceeded";
  const tone = exceeded ? "border-rose-300 bg-rose-50 text-rose-900" : st.forced ? "border-emerald-300 bg-emerald-50 text-emerald-900" : st.level === "warning" ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-800";
  const bar = exceeded ? "bg-rose-500" : st.forced ? "bg-emerald-500" : st.level === "warning" ? "bg-amber-500" : "bg-slate-400";
  const fmtDate = (iso) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "");
  const toggle = async (v) => {
    setBusy(true);
    try {
      const r = await apiClient.put(`/companies/${companyId}/vat/reverse-charge-settings`, { force_booking: v });
      setSt(r.data);
      toast.success(v ? "Bezugsteuer-Buchung manuell aktiviert – ab jetzt wird 8.1 % gebucht" : "Manuelle Aktivierung aufgehoben – es wird wieder nur gezählt");
    } catch (e) { toast.error(e.response?.data?.detail || "Konnte nicht speichern"); } finally { setBusy(false); }
  };
  const downloadReport = async (year = st.year) => {
    setBusy(true);
    try {
      const r = await apiClient.get(`/companies/${companyId}/vat/reverse-charge-report.pdf?year=${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `Bezugsteuer-Meldung-ESTV-${year}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error("PDF konnte nicht erstellt werden"); } finally { setBusy(false); }
  };
  const markSubmitted = async (submitted) => {
    setBusy(true);
    try {
      const r = await apiClient.put(`/companies/${companyId}/vat/reverse-charge-report/submitted?year=${due.year}`, { submitted });
      setSt((s) => ({ ...s, report_due: r.data }));
      toast.success(submitted ? `Bezugsteuer-Meldung ${due.year} als eingereicht markiert – keine weiteren Erinnerungen` : "Markierung aufgehoben");
    } catch (e) { toast.error(e.response?.data?.detail || "Konnte nicht speichern"); } finally { setBusy(false); }
  };
  const bookPayment = async () => {
    setBusy(true);
    try {
      const body = { date: pay.date, bank_account_id: pay.bank_account_id || undefined, reference: pay.reference || undefined };
      if (pay.amount !== "") body.amount = Number(pay.amount);
      const r = await apiClient.post(`/companies/${companyId}/vat/reverse-charge-report/pay?year=${due.year}`, body);
      setSt((s) => ({ ...s, report_due: r.data }));
      setPayOpen(false);
      toast.success(`Bezugsteuer ${due.year} als Zahlung gebucht (Soll 2202 / Haben ${pay.bank_account_id || "1020"})`);
      window.dispatchEvent(new Event("inbox:changed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Zahlung konnte nicht gebucht werden"); } finally { setBusy(false); }
  };
  const dueTone = due?.submitted ? "border-emerald-300 bg-emerald-50 text-emerald-900" : due?.overdue || (due && due.days_left <= 3) ? "border-rose-400 bg-rose-100 text-rose-900" : "border-amber-300 bg-amber-50 text-amber-900";
  return (
    <div className="space-y-2">
    {due && (
      <div className={`rounded-lg border px-4 py-3 text-sm ${dueTone}`} data-testid="reverse-charge-report-due-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            {due.submitted ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <CalendarClock className="h-4 w-4 mt-0.5 shrink-0" />}
            <div className="min-w-0">
              <div className="font-semibold" data-testid="reverse-charge-report-due-title">
                {due.submitted ? `Bezugsteuer-Meldung ${due.year} eingereicht` : due.overdue ? `Bezugsteuer-Meldung ${due.year} überfällig – Frist ${fmtDate(due.deadline)} verstrichen`
                  : `Bezugsteuer-Meldung ${due.year} bis ${fmtDate(due.deadline)} an die ESTV einreichen (noch ${due.days_left} Tage)`}
              </div>
              <div className="text-xs mt-0.5" data-testid="reverse-charge-report-due-hint">
                {due.submitted ? `Markiert am ${fmtDate(due.submitted_at)} von ${due.submitted_by || "–"}. `
                  : `Schwelle CHF 10'000 im ${due.year} überschritten: ${fmt(due.total_net_chf)} Auslandsleistungen (${due.count} Positionen) · Bezugsteuer 8.1 % = ${fmt(due.tax_chf)}. Erinnerungen per Glocke und E-Mail bis zum Einreichen. `}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => downloadReport(due.year)} disabled={busy} data-testid="reverse-charge-report-due-pdf-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors">
              <FileText className="h-3.5 w-3.5" />ESTV-Meldung {due.year} (PDF)
            </button>
            {!due.paid && due.booked_tax_chf > 0 && (
              <button type="button" onClick={() => { setPay((p) => ({ ...p, amount: String(due.booked_tax_chf) })); setPayOpen((v) => !v); }} disabled={busy} data-testid="reverse-charge-report-pay-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100 transition-colors">
                <Banknote className="h-3.5 w-3.5" />Zahlung an ESTV buchen
              </button>
            )}
            {due.submitted ? (
              <button type="button" onClick={() => markSubmitted(false)} disabled={busy} data-testid="reverse-charge-report-unsubmit-btn"
                className="rounded-md px-2 py-1 text-xs underline-offset-2 hover:underline">Markierung aufheben</button>
            ) : (
              <button type="button" onClick={() => markSubmitted(true)} disabled={busy} data-testid="reverse-charge-report-submit-btn"
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-700 transition-colors">
                <CheckCircle2 className="h-3.5 w-3.5" />Als eingereicht markieren
              </button>
            )}
          </div>
        </div>
        {due.paid && (
          <div className="mt-2 text-xs flex items-center gap-1.5" data-testid="reverse-charge-report-paid-hint">
            <CheckCircle2 className="h-3.5 w-3.5" />Bezahlt am {fmtDate(due.payment?.date)} · {fmt(due.payment?.amount)} · Buchung Soll 2202 / Haben {due.payment?.bank_account_id} (Journal {String(due.payment?.journal_entry_id || "").slice(0, 8)}…)
          </div>
        )}
        {!due.paid && due.unbooked_tax_chf > 0.005 && (
          <div className="mt-2 text-xs" data-testid="reverse-charge-report-unbooked-hint">Hinweis: {fmt(due.unbooked_tax_chf)} Bezugsteuer sind erst gezählt, noch nicht als Verbindlichkeit gebucht – Betrag bei der Zahlung prüfen.</div>
        )}
        {payOpen && !due.paid && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end rounded-md border border-slate-200 bg-white p-3" data-testid="reverse-charge-pay-form">
            <label className="text-xs text-slate-600">Zahlungsdatum<SwissDateField wrapClassName="mt-1"  value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} data-testid="reverse-charge-pay-date" /></label>
            <label className="text-xs text-slate-600">Bankkonto<input value={pay.bank_account_id} onChange={(e) => setPay({ ...pay, bank_account_id: e.target.value })} className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-900" data-testid="reverse-charge-pay-bank" /></label>
            <label className="text-xs text-slate-600">Betrag CHF<input type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-900" data-testid="reverse-charge-pay-amount" /></label>
            <label className="text-xs text-slate-600">ESTV-Referenz<input value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="Verfügung Nr." className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm text-slate-900" data-testid="reverse-charge-pay-ref" /></label>
            <button type="button" onClick={bookPayment} disabled={busy || !pay.date} data-testid="reverse-charge-pay-submit"
              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 disabled:opacity-50">Buchen: 2202 an {pay.bank_account_id || "1020"}</button>
          </div>
        )}
      </div>
    )}
    {(compact || st.count > 0 || st.forced) && (
    <div className={`rounded-lg border px-4 py-3 text-sm ${tone}`} data-testid="reverse-charge-threshold-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {exceeded ? <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" /> : <Scale className="h-4 w-4 mt-0.5 shrink-0" />}
          <div className="min-w-0">
            <div className="font-semibold" data-testid="reverse-charge-threshold-title">
              {exceeded ? `Bezugsteuer ist aktiviert – ab jetzt Pflicht (${st.year})` : st.forced ? `Bezugsteuer ${st.year}: manuell aktiviert` : `Bezugsteuer ${st.year}: ${fmt(st.total_net_chf)} von ${fmt(st.threshold_chf)} gezählt`}
            </div>
            <div className="text-xs mt-0.5" data-testid="reverse-charge-threshold-hint">{st.hint}{!exceeded && !st.forced ? ` Markierte Belege: ${st.count}.` : ""}</div>
          </div>
        </div>
        {!exceeded && (
          <label className="flex items-center gap-2 text-xs whitespace-nowrap cursor-pointer" title="Freiwillig schon vor der Schwelle buchen (Treuhänder/Kunde)">
            <Switch checked={!!st.forced} disabled={busy} onCheckedChange={toggle} data-testid="reverse-charge-force-switch" />
            Jetzt schon buchen
          </label>
        )}
        {st.count > 0 && (
          <button type="button" onClick={() => downloadReport()} disabled={busy} data-testid="reverse-charge-report-pdf-btn"
            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${exceeded ? "border-rose-400 bg-white text-rose-800 hover:bg-rose-100" : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100"}`}
            title={`Meldung und Deklaration der Bezugsteuer ${st.year} an die ESTV (Frist 01.03.${st.year + 1})`}>
            <FileText className="h-3.5 w-3.5" />{exceeded ? `ESTV-Meldung ${st.year} (PDF) – bis 01.03.${st.year + 1}` : `ESTV-Meldung ${st.year} (PDF)`}
          </button>
        )}
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-white/70 overflow-hidden">
        <div className={`h-full ${bar} transition-[width] duration-500`} style={{ width: `${Math.min(100, st.percent || 0)}%` }} />
      </div>
    </div>
    )}
    </div>
  );
};

export default ReverseChargeThresholdCard;
